import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Progress, Alert, Space, Spin, Empty, Button, Statistic, Tooltip } from 'antd';
import { ThunderboltOutlined, DatabaseOutlined, GlobalOutlined, ReloadOutlined, DashboardOutlined } from '@ant-design/icons';
import socketService from '../services/socket';
import { agentsAPI } from '../services/api';
import { formatLoad, parseLoadFromResources } from '../utils/loadAverage';

const AgentResourceStats = ({ agentId }) => {
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestedRef = useRef(false);

  const requestResources = useCallback(() => {
    if (!agentId) return;
    agentsAPI.sendCommand(agentId, {
      command_type: 'get-resources',
      data: {}
    }).then(() => {
      requestedRef.current = true;
    }).catch((err) => {
      console.warn('Failed to request resources from agent:', err?.response?.data?.message || err.message);
    });
  }, [agentId]);

  useEffect(() => {
    if (!agentId) return;

    setLoading(true);
    setResources(null);
    setError(null);
    requestedRef.current = false;

    const handler = (data) => {
      if (data.agent_id === agentId) {
        setResources(data);
        setLoading(false);
        setError(null);
      }
    };

    const socket = socketService.getSocket();
    if (socket) {
      socket.on('agent-resources-update', handler);
    }

    requestResources();

    const retryInterval = setInterval(() => {
      requestResources();
    }, 30000);

    const removeConnectListener = socketService.onConnect(() => {
      const s = socketService.getSocket();
      if (s) {
        s.on('agent-resources-update', handler);
      }
      requestResources();
    });

    const initialTimeout = setTimeout(() => {
      setLoading(false);
    }, 20000);

    return () => {
      const s = socketService.getSocket();
      if (s) {
        s.off('agent-resources-update', handler);
      }
      clearInterval(retryInterval);
      clearTimeout(initialTimeout);
      removeConnectListener();
    };
  }, [agentId, requestResources]);

  const pct = (val) => (typeof val === 'number' ? val : 0);

  const getProgressColor = (percent) => {
    const p = pct(percent);
    if (p >= 90) return '#ff4d4f';
    if (p >= 70) return '#faad14';
    if (p >= 50) return '#fa8c16';
    return '#52c41a';
  };

  const getStatusFromPercent = (percent, type) => {
    const p = pct(percent);
    const thresholds = {
      cpu: { warning: 80, critical: 90 },
      memory: { warning: 85, critical: 95 },
      disk: { warning: 85, critical: 95 }
    };

    const threshold = thresholds[type] || thresholds.cpu;
    if (p >= threshold.critical) return { status: 'critical', color: '#ff4d4f', message: `${type.toUpperCase()} usage is critical!` };
    if (p >= threshold.warning) return { status: 'warning', color: '#faad14', message: `${type.toUpperCase()} usage is high.` };
    return { status: 'normal', color: '#52c41a', message: `${type.toUpperCase()} usage is normal.` };
  };

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const cpuPct = pct(resources?.cpu?.usage_percent);
  const memPct = pct(resources?.memory?.usage_percent);
  const diskPct = pct(resources?.disk?.usage_percent);

  const cpuStatus = resources ? getStatusFromPercent(cpuPct, 'cpu') : null;
  const memoryStatus = resources ? getStatusFromPercent(memPct, 'memory') : null;
  const diskStatus = resources ? getStatusFromPercent(diskPct, 'disk') : null;
  const load = resources ? parseLoadFromResources(resources) : null;

  if (error) {
    return (
      <Card title={<Space><ThunderboltOutlined /> Resource Statistics</Space>}>
        <Alert
          message={error}
          type="error"
          showIcon
          action={<Button size="small" icon={<ReloadOutlined />} onClick={requestResources}>Retry</Button>}
        />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card title={<Space><ThunderboltOutlined /> Resource Statistics</Space>}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#888' }}>
            Waiting for agent resource data...
            {!socketService.isConnected() && <><br/><span style={{ color: '#faad14' }}>WebSocket not connected — reconnecting...</span></>}
          </p>
        </div>
      </Card>
    );
  }

  if (!resources) {
    return (
      <Card title={<Space><ThunderboltOutlined /> Resource Statistics</Space>}>
        <Empty description="No resource data received yet. Make sure the agent is online and sending resource updates.">
          <Button type="primary" icon={<ReloadOutlined />} onClick={requestResources}>
            Request Resources
          </Button>
        </Empty>
      </Card>
    );
  }

  return (
    <div>
      {/* Compact Single Row Stats */}
      <Row gutter={[12, 12]}>
        {/* CPU */}
        <Col xs={24} sm={8}>
          <Card size="small" styles={{ body: { padding: '12px 16px' } }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Space><GlobalOutlined /> <strong>CPU</strong></Space>
              <span style={{ fontSize: 20, fontWeight: 600, color: cpuStatus?.color }}>{cpuPct.toFixed(1)}%</span>
            </div>
            <Progress
              percent={Math.round(cpuPct)}
              strokeColor={getProgressColor(cpuPct)}
              showInfo={false}
              size="small"
            />
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              {resources?.cpu?.count || '-'} cores · {resources?.cpu?.model?.substring(0, 30) || '-'}
            </div>
          </Card>
        </Col>

        {/* Memory */}
        <Col xs={24} sm={8}>
          <Card size="small" styles={{ body: { padding: '12px 16px' } }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Space><DatabaseOutlined /> <strong>Memory</strong></Space>
              <span style={{ fontSize: 20, fontWeight: 600, color: memoryStatus?.color }}>{memPct.toFixed(1)}%</span>
            </div>
            <Progress
              percent={Math.round(memPct)}
              strokeColor={getProgressColor(memPct)}
              showInfo={false}
              size="small"
            />
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              {resources?.memory?.used_mb ? (resources.memory.used_mb / 1024).toFixed(1) : '0'} GB / {resources?.memory?.total_mb ? (resources.memory.total_mb / 1024).toFixed(1) : '0'} GB
            </div>
          </Card>
        </Col>

        {/* Disk */}
        <Col xs={24} sm={8}>
          <Card size="small" styles={{ body: { padding: '12px 16px' } }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Space><DatabaseOutlined /> <strong>Disk</strong></Space>
              <span style={{ fontSize: 20, fontWeight: 600, color: diskStatus?.color }}>{diskPct.toFixed(1)}%</span>
            </div>
            <Progress
              percent={Math.round(diskPct)}
              strokeColor={getProgressColor(diskPct)}
              showInfo={false}
              size="small"
            />
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              {resources?.disk?.used_gb || 0} GB / {resources?.disk?.total_gb || 0} GB · {resources?.disk?.path || '-'}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Load average (1m / 5m / 15m — Node.js os.loadavg) */}
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col span={24}>
          <Card
            size="small"
            title={
              <Space>
                <DashboardOutlined />
                <span>Load Average</span>
                {load && (
                  <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>
                    (per CPU: {formatLoad(resources?.system?.load_per_cpu)} · {load.cpuCount} cores)
                  </span>
                )}
              </Space>
            }
            styles={{ body: { padding: '12px 16px' } }}
          >
            {resources?.system?.platform === 'win32' ? (
              <span style={{ color: '#bbb', fontSize: 12 }}>Load average tidak tersedia di Windows</span>
            ) : load ? (
              <Row gutter={16}>
                <Col xs={8}>
                  <Tooltip title="Load 1 menit · hijau di bawah 0.7, kuning 0.7–1.0, merah di atas 1.0 (per CPU)">
                    <Statistic
                      title="1 min"
                      value={formatLoad(load.m1)}
                      valueStyle={{ color: load.color1, fontSize: 22 }}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8}>
                  <Tooltip title="Load 5 menit">
                    <Statistic
                      title="5 min"
                      value={formatLoad(load.m5)}
                      valueStyle={{ color: load.color5, fontSize: 22 }}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8}>
                  <Tooltip title="Rata-rata 15 menit — ini yang disediakan Linux/Node (bukan 10 menit)">
                    <Statistic
                      title="15 min"
                      value={formatLoad(load.m15)}
                      valueStyle={{ color: load.color15, fontSize: 22 }}
                    />
                  </Tooltip>
                </Col>
              </Row>
            ) : (
              <span style={{ color: '#bbb', fontSize: 12 }}>Load average belum tersedia</span>
            )}
          </Card>
        </Col>
      </Row>

      {/* System Info Row */}
      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} sm={12}>
          <Card size="small" styles={{ body: { padding: '10px 16px' } }}>
            <div style={{ fontSize: 12, color: '#888' }}>
              <strong>Hostname:</strong> {resources?.system?.hostname || '-'}<br/>
              <strong>Platform:</strong> {resources?.system?.platform || '-'}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card size="small" styles={{ body: { padding: '10px 16px' } }}>
            <div style={{ fontSize: 12, color: '#888' }}>
              <strong>Uptime:</strong> {formatUptime(resources?.system?.uptime)}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Partition Details - Collapsed by default */}
      {resources?.disk?.partitions?.length > 0 && (
        <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: '8px 16px' } }}>
          <div style={{ fontSize: 11, color: '#666' }}>
            <strong>Partitions:</strong>
            <Row gutter={[8, 4]} style={{ marginTop: 6 }}>
              {resources.disk.partitions.map((part, idx) => (
                <Col key={idx} xs={24} sm={12} md={8}>
                  <div style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4 }}>
                    <strong>{part.path}</strong>: {pct(part.usage_percent).toFixed(1)}% ({part.used_gb || 0} / {part.total_gb || 0} GB)
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Card>
      )}

      {/* Disk I/O Row */}
      {resources?.disk_io?.disks?.length > 0 && (() => {
        // Filter: physical whole disks only (no partitions like nvme0n1p1, no loop, no ram, no zram)
        const wholeDisks = resources.disk_io.disks.filter(d => {
          if (d.device.startsWith('loop') || d.device.startsWith('ram') || d.device.startsWith('zram')) return false;
          if (d.device.includes('p') && d.device.match(/p\d+$/)) return false; // nvme0n1p1 partition
          return true;
        });
        return wholeDisks.length > 0 ? (
          <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: '8px 16px' } }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
              <strong>Disk I/O (Read / Write per sec)</strong>
            </div>
            <Row gutter={[8, 4]}>
              {wholeDisks.map((disk, idx) => {
                const fmt = (bytes) => {
                  if (!bytes || bytes === 0) return '0 B/s';
                  if (bytes < 1024) return `${bytes} B/s`;
                  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB/s`;
                  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB/s`;
                  return `${(bytes / 1073741824).toFixed(2)} GB/s`;
                };
                return (
                  <Col key={idx} xs={24} sm={12} md={8}>
                    <div style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4 }}>
                      <strong>{disk.device}</strong>
                      <Row gutter={[4, 2]} style={{ marginTop: 2 }}>
                        <Col span={12}><span style={{ color: '#52c41a', fontSize: 11 }}>R: {fmt(disk.read_bytes_per_sec)}</span></Col>
                        <Col span={12}><span style={{ color: '#ff4d4f', fontSize: 11 }}>W: {fmt(disk.write_bytes_per_sec)}</span></Col>
                      </Row>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card>
        ) : null;
      })()}
    </div>
  );
};

export default AgentResourceStats;
